import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/react-table';
import type { PageData } from '../+data';
import { fuzzySearch, getSearchScore } from './search';
import { getSongTableColumns } from './songTableColumns';
import type { DifficultyConfig } from './types';
import { Stack } from 'styled-system/jsx';
import { Input } from '~/components/ui/input';
import { Table } from '~/components/ui/table';

type Props = {
  songs: PageData['songs'];
};

export function SongsTable({ songs }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [globalFilter, setGlobalFilter] = useState('');
  const [debouncedGlobalFilter, setDebouncedGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedGlobalFilter(globalFilter);
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [globalFilter]);

  const difficultyConfigs = useMemo<DifficultyConfig[]>(
    () => [
      { key: 'normal', param: '1', label: t('song_table.normal') },
      { key: 'hard', param: '2', label: t('song_table.hard') },
      { key: 'expert', param: '3', label: t('song_table.expert') },
      { key: 'master', param: '4', label: t('song_table.master') }
    ],
    [t]
  );

  const columns = useMemo(
    () =>
      getSongTableColumns({
        difficultyConfigs,
        locale,
        t
      }),
    [difficultyConfigs, locale, t]
  );

  const filteredSongs = useMemo(() => {
    if (!debouncedGlobalFilter.trim()) {
      return songs;
    }

    return [...songs]
      .filter((song) => fuzzySearch(song, debouncedGlobalFilter))
      .sort(
        (a, b) =>
          getSearchScore(b, debouncedGlobalFilter) - getSearchScore(a, debouncedGlobalFilter)
      );
  }, [debouncedGlobalFilter, songs]);

  const table = useReactTable({
    data: filteredSongs,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  return (
    <Stack alignItems="center" w="full">
      <Input
        placeholder="Search songs..."
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        mb={4}
      />

      <Table.Root key={locale}>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Fragment key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Fragment>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Fragment key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Fragment>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Stack>
  );
}
